import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";

const AgentRankingDashboard = () => {
  // Sample agent data
  const agents = [
    {
      name: "Faris",
      percentage: 96,
      total: 12000,
      monthly: [
        { month: "Jan", amount: 4000 },
        { month: "Feb", amount: 5000 },
        { month: "Mar", amount: 3000 },
      ],
    },
    {
      name: "Danish",
      percentage: 92,
      total: 11000,
      monthly: [
        { month: "Jan", amount: 3500 },
        { month: "Feb", amount: 4500 },
        { month: "Mar", amount: 3000 },
      ],
    },
    {
      name: "Fakri",
      percentage: 90,
      total: 10000,
      monthly: [
        { month: "Jan", amount: 3000 },
        { month: "Feb", amount: 4000 },
        { month: "Mar", amount: 3000 },
      ],
    },
  ];

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-700">Ranking Report</h2>

        <div className="flex items-center gap-2 border px-3 py-2 rounded-md bg-white">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-500" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="outline-none text-gray-700"
          />
        </div>
      </div>

      {/* Top Agents */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="font-semibold mb-3 text-gray-700">Top 3 Agents</h3>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          {agents.slice(0, 3).map((agent, i) => (
            <li key={i}>
              <span className="font-semibold">Top {i + 1}:</span> {agent.name} —{" "}
              {agent.percentage}%
            </li>
          ))}
        </ul>
      </div>

      {/* Overall Ranking Chart */}
      <div className="bg-white p-5 rounded-lg shadow mb-6">
        <h3 className="font-semibold mb-4 text-gray-700">
          Overall Agent Collection
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={agents}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            barSize={50}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              formatter={(value) => [`RM ${value.toLocaleString()}`, "Collection"]}
            />
            <Bar
              dataKey="total"
              fill="#60A5FA"
              radius={[8, 8, 0, 0]}
              onClick={(data) => setSelectedAgent(data)}
            />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-xs text-gray-500 text-center mt-2">
          Click any bar to see monthly details
        </p>
      </div>

      {/* Table of Agents */}
      <div className="bg-white p-5 rounded-lg shadow mb-6">
        <h3 className="font-semibold mb-3 text-gray-700">
          Agent Collection Summary
        </h3>
        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Total Collection (RM)</th>
              <th className="p-2 border">Performance (%)</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => (
              <tr
                key={agent.name}
                className="hover:bg-blue-50 cursor-pointer"
                onClick={() => setSelectedAgent(agent)}
              >
                <td className="p-2 border text-center">{agent.name}</td>
                <td className="p-2 border text-center">
                  {agent.total.toLocaleString()}
                </td>
                <td className="p-2 border text-center">{agent.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Selected Agent Details */}
      {selectedAgent && (
        <div className="bg-white p-5 rounded-lg shadow">
          <h3 className="font-semibold mb-4 text-gray-700">
            {selectedAgent.name}'s Monthly Collection
          </h3>

          {/* Monthly Bar Chart */}
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={selectedAgent.monthly} barSize={50}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip
                formatter={(value) => [`RM ${value.toLocaleString()}`, "Amount"]}
              />
              <Bar dataKey="amount" fill="#34D399" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          {/* Monthly Table */}
          <table className="w-full border text-sm mt-5">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border">Month</th>
                <th className="p-2 border">Caj (RM)</th>
                <th className="p-2 border">KPI</th>
                <th className="p-2 border">%</th>
              </tr>
            </thead>
            <tbody>
              {selectedAgent.monthly.map((m, idx) => (
                <tr key={idx}>
                  <td className="p-2 border text-center">{m.month}</td>
                  <td className="p-2 border text-center">
                    {m.amount.toLocaleString()}
                  </td>
                  <td className="p-2 border text-center">Target</td>
                  <td className="p-2 border text-center">
                    {Math.round((m.amount / selectedAgent.total) * 100)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AgentRankingDashboard;
